@let r = result();

<div class="rv-layout" [class.rv-layout--empty]="!r.results.length">
  <!-- Side column on a wide panel; above the grid otherwise. -->
  <aside class="rv-rail" aria-label="Summary">
    @if (r.results.length) {
      <app-top-sites [entries]="r.results" [selected]="siteFilter()" (toggle)="toggleSite($event)" (clear)="clearSites()" />
    }

    @if (missingFields().length) {
      <p class="rv-tip">
        Turn on <strong>{{ missingFields().join(', ') }}</strong> in Response configuration to see them on each article.
      </p>
    }
  </aside>

  <div class="rv-main">
    <div class="rv-toolbar">
      <span class="sr-count">
        <span class="rv-key" appFieldTip="meta.count" [tipText]="tips.count"><strong>{{ r.meta.count | number }}</strong> article{{ r.meta.count === 1 ? '' : 's' }}</span>
        @if (r.meta.totalMatches !== null && r.meta.totalMatches > r.meta.count) {
          <span class="rv-key" appFieldTip="meta.total_matches" [tipText]="tips.totalMatches">of {{ r.meta.totalMatches | number }} matches</span>
        }
      </span>
      @if (siteFilter().size; as picked) {
        @let n = filteredCards().length;
        <span class="rv-filter" role="status">
          {{ n }} article{{ n === 1 ? '' : 's' }} from
          @if (picked === 1) {
            <strong>{{ siteFilter().values().next().value }}</strong>
          } @else {
            <strong>{{ picked }} sites</strong>
          }
          <button type="button" class="rv-filter__clear" (click)="clearSites()" aria-label="Show articles from all sites">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        </span>
      } @else if (sortHint(); as hint) {
        <span class="rv-toolbar__hint">{{ hint }}</span>
      }
    </div>

    @if (!r.results.length) {
      <div class="rv-empty">
        <svg class="rv-empty__icon" width="40" height="40" viewBox="0 0 48 48" fill="none" aria-hidden="true"><circle cx="21" cy="21" r="12" stroke="currentColor" stroke-width="2"/><path d="M30 30l9 9M16 21h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        <p class="rv-empty__title">No articles matched this query.</p>
        <p class="rv-empty__desc">Try a wider date range, fewer filters, or different keywords.</p>
      </div>
    } @else {
      <ol class="sr-grid">
        @for (c of shownCards(); track c.index) {
          @let h = c.hit;
          @let media = hasImage(c);
          @let full = contentOpen().has(c.index);
          <li class="sr-card" [class.sr-card--media]="media">
            @if (media) {
              <a class="sr-card__media" [href]="h.url" target="_blank" rel="noopener noreferrer" tabindex="-1" aria-hidden="true"
                 appFieldTip="image_url" [tipText]="tips.imageUrl" [tipDelay]="500">
                <img [src]="h.imageUrl" alt="" loading="lazy" (error)="imageFailed(c.index)" />
              </a>
              @if (h.relevanceScore !== null) {
                <span class="rv-score sr-card__score" tabindex="0" appFieldTip="relevance_score" [tipText]="tips.relevanceScore"><span class="rv-score__dot"></span>{{ h.relevanceScore | number:'1.0-2' }}</span>
              }
            }

            <div class="sr-card__body">
              @if (h.publicationDate || h.indexationDate || (!media && h.relevanceScore !== null)) {
                <div class="sr-card__dates">
                  @if (h.publicationDate) {
                    <time class="rv-key" [attr.datetime]="h.publicationDate" appFieldTip="publication_date" [tipText]="tips.publicationDate">{{ h.publicationDate | date:'mediumDate' }}</time>
                  }
                  @if (h.indexationDate) {
                    @if (h.publicationDate) { <span class="sr-card__dot" aria-hidden="true">·</span> }
                    <span class="rv-key" appFieldTip="indexation_date" [tipText]="tips.indexationDate">Indexed {{ h.indexationDate | date:'mediumDate' }}</span>
                  }
                  @if (!media && h.relevanceScore !== null) {
                    <span class="rv-score sr-card__score--inline" tabindex="0" appFieldTip="relevance_score" [tipText]="tips.relevanceScore"><span class="rv-score__dot"></span>{{ h.relevanceScore | number:'1.0-2' }}</span>
                  }
                </div>
              }

              @if (h.title) {
                <a class="sr-card__title" [href]="h.url" target="_blank" rel="noopener noreferrer"
                   appFieldTip="title" [tipText]="tips.title" [tipDelay]="500">{{ h.title }}</a>
              }

              @if (h.description) {
                <p class="sr-card__desc" appFieldTip="description" [tipText]="tips.description" [tipDelay]="900">{{ h.description }}</p>
              }

              @if (h.author) {
                <p class="sr-card__author rv-key" appFieldTip="author" [tipText]="tips.author">By {{ h.author }}</p>
              }

              @if (h.content) {
                <button type="button" class="sr-card__action" (click)="toggleContent(c.index)" [attr.aria-expanded]="full"
                        appFieldTip="content" [tipText]="tips.content">
                  {{ full ? 'Hide full text' : 'Show full text' }}
                </button>
                @if (full) {
                  <div class="sr-card__content" tabindex="0" role="region" aria-label="Full text">{{ h.content }}</div>
                }
              }

              <div class="sr-card__foot">
                <a class="sr-card__site" [href]="h.url" target="_blank" rel="noopener noreferrer"
                   [appFieldTip]="h.domainName ? 'domain_name' : 'url'"
                   [tipText]="(h.domainName ? tips.domainName : tips.url) + ' ' + h.url">
                  @if (h.favicon) {
                    <img class="rv-favicon" [src]="h.favicon" alt="" width="14" height="14" loading="lazy" (error)="hideBrokenImage($event)" />
                  }
                  <span class="sr-card__host">{{ h.host }}</span>
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M4.5 2.5h5v5M9.5 2.5L3 9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  <span class="sr-only">(opens the original article)</span>
                </a>
                @if (h.languages.length) {
                  <span class="sr-card__lang rv-key" appFieldTip="language" [tipText]="tips.language">{{ h.languages.join(', ') }}</span>
                }
              </div>
            </div>
          </li>
        }
      </ol>

      @if (filteredCards().length > shownCards().length) {
        <button type="button" class="btn--secondary rv-more" (click)="showMore()">
          Show more ({{ filteredCards().length - shownCards().length }} left)
        </button>
      }
    }
  </div>
</div>
